const STORAGE_PREFIX = 'noonaway:collection-return:';
const PRODUCT_LINK_SELECTOR = '.product-card a[href*="/products/"]';
const PRODUCT_CARD_SELECTOR = '#ProductGridContainer motion-list .product-card';
const LOAD_MORE_SELECTOR = '#ProductGridContainer button[is="infinite-button"]';
let restoreInProgress = false;

export function canonicalCollectionUrl(input, base = 'https://noonawayart.com') {
  const url = new URL(input, base);
  url.searchParams.delete('page');
  url.searchParams.delete('section_id');
  url.searchParams.sort();
  return `${url.pathname}${url.search}`;
}

export function createStorageKey(input) {
  return `${STORAGE_PREFIX}${canonicalCollectionUrl(input)}`;
}

export function shouldRestore(state, historyState, currentUrl) {
  if (!state || historyState?.noonawayCollectionReturn !== true) return false;
  if (!state.productUrl || !Number.isFinite(state.scrollY)) return false;
  return canonicalCollectionUrl(state.collectionUrl) === canonicalCollectionUrl(currentUrl);
}

function productPath(input) {
  const url = new URL(input, window.location.origin);
  return `${url.pathname}${url.search}`;
}

function getStoredState() {
  try {
    const value = window.sessionStorage.getItem(createStorageKey(window.location.href));
    return value ? JSON.parse(value) : null;
  }
  catch (error) {
    return null;
  }
}

function saveState(state) {
  try {
    window.sessionStorage.setItem(createStorageKey(state.collectionUrl), JSON.stringify(state));
  }
  catch (error) {
    return;
  }
}

function findProductLink(productUrl) {
  return Array.from(document.querySelectorAll(PRODUCT_LINK_SELECTOR)).find((link) => productPath(link.href) === productUrl);
}

function waitForMoreProducts(previousCount, timeout = 8000) {
  return new Promise((resolve) => {
    const container = document.getElementById('ProductGridContainer');
    if (!container) {
      resolve(false);
      return;
    }

    const finish = (value) => {
      observer.disconnect();
      window.clearTimeout(timer);
      resolve(value);
    };
    const observer = new MutationObserver(() => {
      if (document.querySelectorAll(PRODUCT_CARD_SELECTOR).length > previousCount) finish(true);
      const button = document.querySelector(LOAD_MORE_SELECTOR);
      if (button && !button.hasAttribute('aria-busy') && button.dataset.loadFailed === 'true') finish(false);
    });
    const timer = window.setTimeout(() => finish(false), timeout);
    observer.observe(container, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-busy', 'data-load-failed'] });
  });
}

async function restoreCollectionPosition() {
  if (restoreInProgress) return;
  const state = getStoredState();
  if (!shouldRestore(state, window.history.state, window.location.href)) return;

  restoreInProgress = true;

  try {
    const targetCount = Math.min(Math.max(Number(state.loadedCardCount) || 0, 0), 1000);
    let attempts = 0;
    let targetLink = findProductLink(state.productUrl);

    while (!targetLink && document.querySelectorAll(PRODUCT_CARD_SELECTOR).length < targetCount && attempts < 50) {
      const button = document.querySelector(LOAD_MORE_SELECTOR);
      if (!button) break;
      const previousCount = document.querySelectorAll(PRODUCT_CARD_SELECTOR).length;
      button.click();
      const loaded = await waitForMoreProducts(previousCount);
      if (!loaded) break;
      attempts += 1;
      targetLink = findProductLink(state.productUrl);
    }

    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    if (Number.isFinite(state.scrollY)) {
      window.scrollTo({ top: state.scrollY, left: 0, behavior: 'auto' });
    }
    else if (targetLink) {
      targetLink.closest('.product-card')?.scrollIntoView({ block: 'center', behavior: 'auto' });
    }

    const nextHistoryState = { ...window.history.state };
    delete nextHistoryState.noonawayCollectionReturn;
    window.history.replaceState(nextHistoryState, '', window.location.href);
  }
  finally {
    restoreInProgress = false;
  }
}

function recordCollectionPosition(event) {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const link = event.target.closest(PRODUCT_LINK_SELECTOR);
  if (!link) return;

  const destination = new URL(link.href, window.location.origin);
  if (destination.origin !== window.location.origin) return;

  const state = {
    collectionUrl: canonicalCollectionUrl(window.location.href),
    productUrl: `${destination.pathname}${destination.search}`,
    loadedCardCount: document.querySelectorAll(PRODUCT_CARD_SELECTOR).length,
    scrollY: window.scrollY,
    savedAt: Date.now()
  };

  saveState(state);
  window.history.replaceState({ ...window.history.state, noonawayCollectionReturn: true }, '', window.location.href);
}

function initCollectionHistory() {
  document.addEventListener('click', recordCollectionPosition, true);
  window.addEventListener('pageshow', restoreCollectionPosition);
  restoreCollectionPosition();
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCollectionHistory, { once: true });
  }
  else {
    initCollectionHistory();
  }
}
